import { c as _c } from "react/compiler-runtime";
import { useRechartsOffset as useOffset, useRechartsPlotArea as usePlotArea, type RechartsNumberDomain as NumberDomain } from '@precis-digital/kurama/recharts';
import { useState, type ReactElement } from 'react';
import { useZoomDomainMinChange } from '../hooks';
import { useChartElementContext } from '../hooks/chartElementContext';
import type { ChartCategoricalDomain as CategoricalDomain, DimensionOrMetricDomain, MetricDomain } from '../internalTypes';
import { calculateZoomPointerEventValues, getFirstAndSecondDomainsHorizontal, getFirstAndSecondDomainsVertical, getReferenceElement, getXAndYDomainsAndReverseOnMove, getXAndYStartAndDomainsOnStart, type Area } from '../utils/zoom';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
export type ChartZoomOverlayOnZoomHandler = (domains: {
  firstDomain: DimensionOrMetricDomain;
  secondDomain: MetricDomain;
  otherSecondDomain: MetricDomain | null;
}) => void;
interface ChartZoomOverlayProps {
  onZoom: ChartZoomOverlayOnZoomHandler;
  setZooming: (zooming: boolean) => void;
  tooltipElement?: ReactElement | null;
  drawingElements: ReactElement[];
}
const ChartZoomOverlay = t0 => {
  const $ = _c(54);
  const {
    onZoom,
    setZooming,
    tooltipElement,
    drawingElements
  } = t0;
  const defaults = useChartCardDefaults();
  const {
    layout,
    chartType,
    chartAxisIdsAndDomains
  } = useChartElementContext();
  const isScatter = chartType === "scatter";
  const isHorizontal = layout === "horizontal";
  const plotArea = usePlotArea();
  const offset = useOffset();
  const chartWidth = (plotArea?.width ?? 0) + (offset?.left ?? 0) + (offset?.right ?? 0);
  const chartHeight = (plotArea?.height ?? 0) + (offset?.top ?? 0) + (offset?.bottom ?? 0);
  const {
    firstAxisDomain,
    secondAxisDomain,
    otherSecondAxisDomain,
    xAxisId,
    xAxisDomain,
    yAxisId,
    yAxisDomain
  } = chartAxisIdsAndDomains;
  const xAxisMinChange = useZoomDomainMinChange(xAxisDomain);
  const yAxisMinChange = useZoomDomainMinChange(yAxisDomain);
  const firstAxisCategoricalDomain = isScatter ? undefined : firstAxisDomain as CategoricalDomain;
  const firstAxisNumberDomain = isScatter ? firstAxisDomain as NumberDomain : undefined;
  const [refArea, setRefArea] = useState(null);
  let t1;
  if ($[0] !== firstAxisCategoricalDomain || $[1] !== firstAxisNumberDomain || $[2] !== isScatter || $[3] !== layout || $[4] !== offset || $[5] !== otherSecondAxisDomain || $[6] !== plotArea || $[7] !== secondAxisDomain || $[8] !== setZooming) {
    t1 = e => {
      const {
        pixelX,
        pixelY,
        firstAxisValue,
        secondAxisValue,
        otherSecondAxisValue,
        singleAxis
      } = calculateZoomPointerEventValues({
        e,
        offset,
        plotArea,
        layout,
        isScatter,
        firstAxisNumberDomain,
        firstAxisCategoricalDomain,
        secondAxisDomain,
        otherSecondAxisDomain
      });
      if (firstAxisValue == null || secondAxisValue == null || otherSecondAxisDomain != null && otherSecondAxisValue == null) {
        return;
      }
      e.currentTarget.setPointerCapture(e.pointerId);
      setZooming(true);
      setRefArea({
        xReverse: false,
        yReverse: false,
        ...getXAndYStartAndDomainsOnStart({
          layout,
          firstAxisValue,
          secondAxisValue,
          pixelX,
          pixelY
        }),
        otherDomain: otherSecondAxisValue == null ? null : [otherSecondAxisValue, otherSecondAxisValue],
        singleAxis
      });
    };
    $[0] = firstAxisCategoricalDomain;
    $[1] = firstAxisNumberDomain;
    $[2] = isScatter;
    $[3] = layout;
    $[4] = offset;
    $[5] = otherSecondAxisDomain;
    $[6] = plotArea;
    $[7] = secondAxisDomain;
    $[8] = setZooming;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const handlePointerDown = t1;
  let t2;
  if ($[10] !== firstAxisCategoricalDomain || $[11] !== firstAxisNumberDomain || $[12] !== isScatter || $[13] !== layout || $[14] !== offset || $[15] !== otherSecondAxisDomain || $[16] !== plotArea || $[17] !== refArea || $[18] !== secondAxisDomain || $[19] !== xAxisMinChange || $[20] !== yAxisMinChange) {
    t2 = e_0 => {
      if (refArea == null) {
        return;
      }
      const {
        pixelX: pixelX_0,
        pixelY: pixelY_0,
        firstAxisValue: firstAxisValue_0,
        secondAxisValue: secondAxisValue_0,
        otherSecondAxisValue: otherSecondAxisValue_0
      } = calculateZoomPointerEventValues({
        e: e_0,
        offset,
        plotArea,
        layout,
        isScatter,
        firstAxisNumberDomain,
        firstAxisCategoricalDomain,
        secondAxisDomain,
        otherSecondAxisDomain,
        singleAxis: refArea.singleAxis
      });
      setRefArea({
        ...refArea,
        ...getXAndYDomainsAndReverseOnMove({
          layout,
          xDomain: refArea.xDomain,
          yDomain: refArea.yDomain,
          pixelX: pixelX_0,
          pixelY: pixelY_0,
          xStart: refArea.xStart,
          yStart: refArea.yStart,
          xAxisMinChange,
          yAxisMinChange,
          firstAxisValue: firstAxisValue_0,
          secondAxisValue: secondAxisValue_0 ?? otherSecondAxisValue_0,
          singleAxis: refArea.singleAxis
        }),
        otherDomain: refArea.otherDomain == null || otherSecondAxisValue_0 == null ? null : [refArea.otherDomain[0], otherSecondAxisValue_0]
      });
    };
    $[10] = firstAxisCategoricalDomain;
    $[11] = firstAxisNumberDomain;
    $[12] = isScatter;
    $[13] = layout;
    $[14] = offset;
    $[15] = otherSecondAxisDomain;
    $[16] = plotArea;
    $[17] = refArea;
    $[18] = secondAxisDomain;
    $[19] = xAxisMinChange;
    $[20] = yAxisMinChange;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  const handlePointerMove = t2;
  let t3;
  if ($[22] !== firstAxisCategoricalDomain || $[23] !== isHorizontal || $[24] !== onZoom || $[25] !== refArea || $[26] !== setZooming) {
    t3 = e_1 => {
      e_1.currentTarget.releasePointerCapture(e_1.pointerId);
      if (refArea == null) {
        return;
      }
      setRefArea(null);
      setZooming(false);
      let {
        xDomain,
        yDomain,
        otherDomain,
        xReverse,
        yReverse,
        singleAxis: singleAxis_0
      } = refArea;
      const xSingle = xDomain[0] === xDomain[1];
      const ySingle = yDomain[0] === yDomain[1];
      if (xSingle && ySingle) {
        return;
      }
      if (xReverse) {
        xDomain = [xDomain[1], xDomain[0]] as CategoricalDomain | NumberDomain;
      }
      if (yReverse) {
        yDomain = [yDomain[1], yDomain[0]] as CategoricalDomain | NumberDomain;
      }
      const {
        firstDomain,
        secondDomain
      } = isHorizontal ? getFirstAndSecondDomainsHorizontal({
        firstAxisCategoricalDomain,
        xSingle,
        ySingle,
        xDomain,
        yDomain
      }) : getFirstAndSecondDomainsVertical({
        firstAxisCategoricalDomain,
        xSingle,
        ySingle,
        xDomain,
        yDomain
      });
      if (otherDomain != null) {
        if (secondDomain == null) {
          otherDomain = null;
        } else {
          if (isHorizontal && yReverse || !isHorizontal && xReverse) {
            otherDomain = [otherDomain[1], otherDomain[0]];
          }
        }
      }
      onZoom({
        firstDomain: singleAxis_0 === "first" || singleAxis_0 == null ? firstDomain : undefined,
        secondDomain: singleAxis_0 === "second" || singleAxis_0 == null ? secondDomain : undefined,
        otherSecondDomain: singleAxis_0 === "otherSecond" || singleAxis_0 == null ? otherDomain : undefined
      });
    };
    $[22] = firstAxisCategoricalDomain;
    $[23] = isHorizontal;
    $[24] = onZoom;
    $[25] = refArea;
    $[26] = setZooming;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  const handlePointerUp = t3;
  let t4;
  if ($[28] !== onZoom) {
    t4 = () => {
      onZoom({
        firstDomain: undefined,
        secondDomain: undefined,
        otherSecondDomain: undefined
      });
    };
    $[28] = onZoom;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  const handleDoubleClick = t4;
  let t5;
  if ($[30] !== defaults || $[31] !== firstAxisCategoricalDomain || $[32] !== firstAxisNumberDomain || $[33] !== isHorizontal || $[34] !== refArea || $[35] !== secondAxisDomain || $[36] !== xAxisId || $[37] !== yAxisId) {
    t5 = getReferenceElement({
      defaults,
      xAxisId,
      yAxisId,
      refArea,
      isHorizontal,
      firstAxisCategoricalDomain,
      firstAxisNumberDomain,
      secondAxisDomain
    });
    $[30] = defaults;
    $[31] = firstAxisCategoricalDomain;
    $[32] = firstAxisNumberDomain;
    $[33] = isHorizontal;
    $[34] = refArea;
    $[35] = secondAxisDomain;
    $[36] = xAxisId;
    $[37] = yAxisId;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  const referenceElement = t5;
  if (plotArea == null) {
    return null;
  }
  const t6 = refArea == null && tooltipElement;
  let t7;
  if ($[39] !== refArea) {
    t7 = refArea == null ? undefined : {
      cursor: "crosshair"
    };
    $[39] = refArea;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  let t8;
  if ($[41] !== chartHeight || $[42] !== chartWidth || $[43] !== handleDoubleClick || $[44] !== handlePointerDown || $[45] !== handlePointerMove || $[46] !== handlePointerUp || $[47] !== t7) {
    t8 = <rect key="plot-area-pointer-events" x={0} y={0} width={chartWidth} height={chartHeight} fill="transparent" style={t7} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onDoubleClick={handleDoubleClick} />;
    $[41] = chartHeight;
    $[42] = chartWidth;
    $[43] = handleDoubleClick;
    $[44] = handlePointerDown;
    $[45] = handlePointerMove;
    $[46] = handlePointerUp;
    $[47] = t7;
    $[48] = t8;
  } else {
    t8 = $[48];
  }
  const t9 = refArea != null && referenceElement;
  let t10;
  if ($[49] !== drawingElements || $[50] !== t6 || $[51] !== t8 || $[52] !== t9) {
    t10 = <>{t6}{drawingElements}{t8}{t9}</>;
    $[49] = drawingElements;
    $[50] = t6;
    $[51] = t8;
    $[52] = t9;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  return t10;
};
export default ChartZoomOverlay;